import { useEffect, useMemo } from "react";
import { t } from "i18next";
import { useAuthStore } from "@/store/auth.store";
import { useUpdateUser } from "@/hooks/user/useUpdateUser";
import { toast } from "@/hooks/use-toast";
import type { AccountSettingSchemaType } from "@/schemas/accountSettings.schema";

import { ProfileForm } from "./components/ProfileForm";
import { useOccupationsStore } from "@/store/occupations.store";

export const ProfileSettings = (): JSX.Element => {
  const { updateUser, loading, result } = useUpdateUser();
  const { userData, getUser } = useAuthStore.getState();
  const {
    fetchAndSetOccupations,
    loading: loadingOccupations,
    occupations,
  } = useOccupationsStore();

  useEffect(() => {
    void fetchAndSetOccupations();
  }, [fetchAndSetOccupations]);

  const defaultValues = useMemo<AccountSettingSchemaType>(
    () => ({
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      phone: userData?.phone || "",
      country: userData?.userPreference.country || "",
      state: userData?.userPreference.state || "",
      city: userData?.userPreference.city || "",
      occupation: userData?.userPreference.occupation || "",
      occupationId: 0,
    }),
    [userData]
  );

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.profile.updatedToast.successful.title"),
          description: t(
            "settings.profile.updatedToast.successful.description"
          ),
        });
        if (userData) void getUser(userData.email);
      } else {
        toast({
          variant: "destructive",
          title: t("settings.profile.updatedToast.error.title"),
          description: t("settings.profile.updatedToast.error.description"),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, loading]);

  const handleSubmit = (data: Partial<AccountSettingSchemaType>): void => {
    if (userData) {
      void updateUser({ userId: userData.id, ...data });
    }
  };

  return (
    <section>
      <ProfileForm
        defaultValues={defaultValues}
        loadingOccupations={loadingOccupations}
        occupations={occupations}
        onSubmit={handleSubmit}
      />
    </section>
  );
};
